<template>
  <main class="vocabulary-library">
    <div class="container-fluid px-3 px-sm-4">
      <div class="row justify-content-center">
        <div class="col-12 vocabulary-library__inner">
          <button
            type="button"
            class="vocabulary-library__back"
            aria-label="بازگشت به صفحه تمرین آیلتس"
            @click="goBack"
          >
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path d="M5 12H19" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M13 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span>بازگشت</span>
          </button>

          <header class="vocabulary-library__header">
            <p class="vocabulary-library__eyebrow">کتابخانه واژگان</p>
            <h1 class="vocabulary-library__title">منبع یادگیری‌ات رو انتخاب کن</h1>
            <p class="vocabulary-library__subtitle">
              یکی از کتاب‌ها رو انتخاب کن تا مسیر واژگان شروع بشه.
            </p>
          </header>

          <div class="row g-4 g-md-4 g-lg-5 vocabulary-library__grid">
            <div
              v-for="book in books"
              :key="book.id"
              class="col-12 col-md-6"
            >
              <BookCard
                :title="book.title"
                :description="book.description"
                :level="book.level"
                :units="book.units"
                :progress="book.progress"
                :cover-color="book.coverColor"
                :cover-title="book.coverTitle"
                :logo="book.logo"
                :publisher="book.publisher"
                @continue="handleContinue(book)"
              />
            </div>
          </div>

          <footer class="vocabulary-library__footer" role="note">
            <span class="vocabulary-library__footer-icon" aria-hidden="true">💡</span>
            <span>هر زمان خواستی می‌تونی بین منابع جابه‌جا بشی.</span>
          </footer>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import { reactive } from "vue";
import { useRouter } from "vue-router";
import BookCard from "@/components/BookCard.vue";

const router = useRouter();

const books = reactive([
  {
    id: "eviu",
    title: "English Vocabulary in Use",
    description: "یادگیری واژگان روزمره انگلیسی در متن واقعی.",
    level: "متوسط تا پیشرفته",
    units: "۱۰۰ واحد",
    progress: 62,
    coverColor: "blue",
    coverTitle: "English\nVocabulary\nin Use",
    logo: "📘 Cambridge",
    publisher: "CambridgePress",
  },
  {
    id: "ielts-vocab",
    title: "Vocabulary for IELTS",
    description: "واژگان ضروری آکادمیک برای موفقیت در آیلتس.",
    level: "آکادمیک",
    units: "۲۵ فصل",
    progress: 18,
    coverColor: "purple",
    coverTitle: "Vocabulary\nfor\nIELTS",
    logo: "📗 Academic",
    publisher: "Academic",
  },
]);

function handleContinue(_book: { id: string; title: string }) {
  router.push("/dashboard/leitner");
}

function goBack() {
  const hasHistory = window.history.state && window.history.state.back;
  if (hasHistory) {
    router.back();
  } else {
    router.push("/dashboard");
  }
}
</script>

<style scoped>
.vocabulary-library {
  --color-primary: #6D5EF8;
  --color-secondary: #6A4328;
  --color-bg: #FFFDF8;
  --color-card: #FFFFFF;
  --color-border: #ECE4D9;
  --color-text: #4C372A;
  --color-muted: #8C8176;
  --color-success: #79C26D;
  --color-warning: #865FEA;

  /* aliases for BookCard child component */
  --vocab-card-bg: var(--color-card);
  --vocab-card-border: var(--color-border);
  --vocab-accent-gold: var(--color-primary);
  --vocab-accent-purple: var(--color-primary);
  --vocab-text-primary: var(--color-text);
  --vocab-text-secondary: var(--color-muted);
  --vocab-text-eyebrow: var(--color-secondary);
  --vocab-pill-bg: #FBF7FC;
  --vocab-progress-track: #F0EBE3;
  --vocab-progress-fill: var(--color-primary);

  min-height: 100vh;
  background: var(--color-bg);
  color: var(--color-text);
  font-family: "Vazirmatn", "Segoe UI", Tahoma, sans-serif;
  padding: 56px 0;
  direction: rtl;
  overflow-x: hidden;
  box-sizing: border-box;
}

.vocabulary-library__inner {
  max-width: 840px;
  margin-inline: auto;
}

.vocabulary-library__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--color-card);
  border: 1px solid var(--color-border);
  color: var(--color-text);
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  padding: 9px 18px;
  border-radius: 999px;
  cursor: pointer;
  margin-bottom: 32px;
  box-shadow: 0 2px 8px rgba(76, 55, 42, 0.06);
  transition: background-color 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}

.vocabulary-library__back svg {
  flex-shrink: 0;
}

.vocabulary-library__back:hover {
  background: #f5f3ff;
  border-color: var(--color-primary);
  transform: translateX(2px);
}

.vocabulary-library__back:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(109, 94, 248, 0.25);
}

.vocabulary-library__header {
  margin-bottom: 36px;
}

.vocabulary-library__eyebrow {
  display: block;
  color: var(--color-secondary);
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 10px;
}

.vocabulary-library__title {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 10px;
  color: var(--color-text);
  line-height: 1.3;
}

.vocabulary-library__subtitle {
  color: var(--color-muted);
  font-size: 15px;
  margin: 0;
  line-height: 1.7;
}

.vocabulary-library__grid {
  margin-bottom: 0;
}

.vocabulary-library__footer {
  margin-top: 24px;
  background: var(--color-card);
  border: 1px solid var(--color-border);
  border-radius: 18px;
  padding: 18px 28px;
  text-align: center;
  color: var(--color-muted);
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 2px 12px rgba(76, 55, 42, 0.05);
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.vocabulary-library__footer:hover {
  border-color: rgba(109, 94, 248, 0.35);
  box-shadow: 0 8px 24px rgba(109, 94, 248, 0.1);
}

.vocabulary-library__footer-icon {
  flex-shrink: 0;
}

@media (min-width: 768px) and (max-width: 991.98px) {
  .vocabulary-library {
    padding: 32px 0;
  }

  .vocabulary-library__grid {
    --bs-gutter-x: 0.875rem;
    --bs-gutter-y: 0.875rem;
  }
}

@media (max-width: 575.98px) {
  .vocabulary-library {
    padding: 28px 0;
  }

  .vocabulary-library__title {
    font-size: 26px;
  }

  .vocabulary-library__subtitle {
    font-size: 14px;
  }
}
</style>
